import {useState, type ReactElement} from 'react'
import {useTranslation} from 'react-i18next'
import {isUpgradeGateRequired} from '@/lib/errors'
import {formatSegmentKey} from '@/lib/segments'
import {useBillingState, type UpgradeProposal} from '@/hooks/inci/cartera/use-billing'
import {UpgradeModal, type UpgradeGate, type UpgradeKind} from '@/components/billing/upgrade-modal'

/** upgrade-gating-spec §1. `catchGate` returns true when the gate handled the error, so onError
 * skips its own toast; `onPaid` receives the band already in customer language. */
export function useUpgradeGate(
  businessId: string,
  kind: UpgradeKind,
  onPaid: (band: string) => void
): {catchGate: (err: unknown, proposal: UpgradeProposal) => boolean; modal: ReactElement} {
  const {t} = useTranslation()
  const [gate, setGate] = useState<UpgradeGate | null>(null)
  const {data: billingState} = useBillingState(gate ? businessId : undefined)

  function catchGate(err: unknown, proposal: UpgradeProposal): boolean {
    if (!isUpgradeGateRequired(err)) return false
    setGate({kind, proposal})
    return true
  }

  const modal = (
    <UpgradeModal
      businessId={businessId}
      gate={gate}
      hasPaymentSource={!!billingState?.payment_source}
      onClose={() => setGate(null)}
      onPaid={(band) => {
        setGate(null)
        onPaid(formatSegmentKey(band, t))
      }}
    />
  )

  return {catchGate, modal}
}
